// Per-PO shipping-instructions editor (2026-07-24, CEO shipping requirements).
//
// A self-contained "Shipping" section for the staff PO detail page, placed
// above the send action so the natural flow is configure-shipping → send. It
// owns everything the vendor needs to fulfil the order EXCEPT multi-location
// (a follow-on build):
//   - Ship to: keep the current destination, re-point at a saved client
//     address, or ship to a vendor/decorator (the SanMar → Huetone leg).
//   - Ship via: carrier + service level (compose into shipping_method server
//     side, "FedEx Ground"), plus an optional client carrier account to bill.
//   - Ship by: the required ship date, with the order's client in-hands date
//     shown read-only alongside.
//   - Shipping notes: the vendor-visible notes field.
//
// All fields save through one PATCH /pos/:id. Edits are gated on canManage
// (admin | production_rep) and — matching the route, which has no stage gate —
// remain allowed after the PO is sent (shipping details routinely change post
// send). Data-fetching is TanStack Query; brand-locked (brown/rust/cream/peach).
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Truck, Pencil } from "lucide-react";
import { OTHER_SHIPPING_ACCOUNT_ZIP, cleanPoNotes, shipDateBeforeInHands } from "@merchd/shared";
import { api, ApiError } from "@/lib/api";
import { CARRIER_OPTIONS, SHIP_SERVICE_LEVELS, carrierLabel } from "@/lib/carriers";

interface ShipAddressSnapshot {
  /** Set on the "as it should read" copy (vendor_ship_to). */
  companyName?: string | null;
  label?: string;
  attention?: string | null;
  street1?: string;
  street2?: string | null;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  countryCode?: string;
  [k: string]: unknown;
}

export interface PoShippingData {
  shippingAddressSnapshot: unknown;
  shippingAddressId: string | null;
  shippingAccountId: string | null;
  shippingMethod: string | null;
  requiredShipDate: string | null;
  notes: string | null;
  shipFromVendorId: string | null;
  poType: string;
  /** The supplier's shipping price, "85.00" (staff only). */
  shippingQuote?: string | null;
  /** The ship-to as it should read (company + Attn); null → the raw snapshot. */
  displayShipTo?: unknown;
}

export interface PoShippingCtx {
  clientId: string;
  vendorName: string;
  inHandsDate: string | null;
  shipFromVendorName: string | null;
}

interface AddressRow {
  id: string;
  label: string;
  attention: string | null;
  street1: string;
  city: string;
  stateCode: string;
  postalCode: string;
}
interface ShippingAccountRow {
  id: string;
  carrier: string;
  accountNumber: string;
  label: string | null;
  isActive: boolean | null;
}
interface VendorRow {
  id: string;
  name: string;
}

// Ship methods (Stephanie 2026-10-14). An older PO's other value (e.g.
// "Freight") still shows as its own option so nothing is lost on edit.
const SERVICE_LEVELS: readonly string[] = SHIP_SERVICE_LEVELS;

/** Whose carrier account pays for the shipping. */
type AccountOwner = "client" | "other" | "supplier";

/** Other's own accounts + the "ship on supplier's account" choice. */
interface HouseAccountRow {
  id: string;
  carrier: string;
  accountNumber: string;
  label: string | null;
  kind: "other" | "supplier";
}

// Split a stored "FedEx Ground" style method back into carrier + service so the
// editor can prefill. Best-effort: if the first token maps to a known carrier
// label, the rest is the service level; otherwise the whole string is service.
function splitMethod(method: string | null): { carrier: string; service: string } {
  if (!method) return { carrier: "", service: "" };
  const trimmed = method.trim();
  for (const o of CARRIER_OPTIONS) {
    if (trimmed.toLowerCase().startsWith(o.label.toLowerCase())) {
      return {
        carrier: o.value,
        service: trimmed.slice(o.label.length).trim(),
      };
    }
  }
  return { carrier: "", service: trimmed };
}

/** YYYY-MM-DD of the day before — the latest allowed ship-by date. */
function dayBefore(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function AddressSummary({ snap }: { snap: ShipAddressSnapshot | null }) {
  if (!snap) return <span className="text-sm italic text-muted">Not set</span>;
  return (
    <div className="text-sm leading-snug text-ink">
      {/* Company, then "Attn: <contact>" — never the address's internal name
          ("Main") (Stephanie 2026-10-27), same as the supplier's PO. */}
      {(() => {
        const heading = snap.companyName || snap.label || snap.attention;
        return (
          <>
            {heading && <p className="font-semibold">{heading}</p>}
            {snap.attention && snap.attention !== heading && <p>Attn: {snap.attention}</p>}
          </>
        );
      })()}
      {snap.street1 && <p>{snap.street1}</p>}
      {(snap.city || snap.stateCode || snap.postalCode) && (
        <p>
          {[snap.city, snap.stateCode, snap.postalCode].filter(Boolean).join(", ")}
        </p>
      )}
    </div>
  );
}

export function PoShippingSection(props: {
  poId: string;
  po: PoShippingData;
  ctx: PoShippingCtx | null;
  canManage: boolean;
  onSaved: () => void;
  /** The "Shipping price" box is switched on (Jake's DDL has run). */
  shippingQuoteReady?: boolean;
}) {
  const { poId, po, ctx, canManage, shippingQuoteReady } = props;
  const snap = ((po.displayShipTo ?? po.shippingAddressSnapshot) ?? null) as ShipAddressSnapshot | null;
  const initial = splitMethod(po.shippingMethod);

  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destMode, setDestMode] = useState<"keep" | "address" | "vendor">("keep");
  const [addressId, setAddressId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [carrier, setCarrier] = useState(initial.carrier);
  const [service, setService] = useState(initial.service);
  const [accountId, setAccountId] = useState(po.shippingAccountId ?? "");
  // Two steps, like commonsku (Stephanie 2026-10-14, Jake approved): whose
  // account first, then which one. Null = not picked yet.
  const [whose, setWhose] = useState<AccountOwner | null>(null);
  // The supplier's shipping price — optional (Stephanie 2026-10-14).
  const [quote, setQuote] = useState(po.shippingQuote ?? "");
  const [requiredShipDate, setRequiredShipDate] = useState(
    po.requiredShipDate ?? "",
  );
  const [notes, setNotes] = useState(cleanPoNotes(po.notes) ?? "");

  const clientId = ctx?.clientId;
  const addresses = useQuery({
    // GET /clients/:id/addresses returns { addresses: [...] } (not { rows }).
    queryKey: ["clients", clientId, "addresses"],
    queryFn: () =>
      api<{ addresses: AddressRow[] }>(`/clients/${clientId}/addresses`),
    enabled: editing && Boolean(clientId),
  });
  const accounts = useQuery({
    queryKey: ["clients", clientId, "shipping-accounts"],
    queryFn: () =>
      api<{ shipping_accounts: ShippingAccountRow[] }>(
        `/clients/${clientId}/shipping-accounts`,
      ),
    enabled: editing && Boolean(clientId),
  });
  // Other's accounts + "Ship on supplier's account" (2026-10-14). Loaded even
  // when not editing so the summary can name the chosen account.
  const house = useQuery({
    queryKey: ["shipping-accounts", "house"],
    queryFn: () =>
      api<{ ready: boolean; shipping_accounts: HouseAccountRow[] }>(`/shipping-accounts/house`),
    staleTime: 5 * 60 * 1000,
  });
  const houseRows = house.data?.shipping_accounts ?? [];
  const supplierChoice = houseRows.find((r) => r.kind === "supplier");
  const otherAccounts = houseRows.filter((r) => r.kind === "other");
  const currentHouse = houseRows.find((r) => r.id === po.shippingAccountId);
  const vendors = useQuery({
    queryKey: ["vendors", "list"],
    queryFn: () => api<{ rows: VendorRow[] }>(`/vendors`),
    enabled: editing && destMode === "vendor",
  });

  const activeAccounts = (accounts.data?.shipping_accounts ?? []).filter(
    (a) => a.isActive !== false || a.id === po.shippingAccountId,
  );
  const currentAccount = activeAccounts.find((a) => a.id === po.shippingAccountId);
  /** Which group an account id belongs to (null when none / unknown yet). */
  const ownerOf = (id: string): AccountOwner | null => {
    if (!id) return null;
    if (supplierChoice?.id === id) return "supplier";
    if (otherAccounts.some((a) => a.id === id)) return "other";
    if (activeAccounts.some((a) => a.id === id)) return "client";
    return null;
  };
  // Other's is the usual choice, so it's first and shown when nothing is
  // picked yet (Stephanie 2026-10-14).
  const shownWhose = whose ?? ownerOf(accountId) ?? "other";
  const pickWhose = (w: AccountOwner): void => {
    setWhose(w);
    if (w === "supplier") setAccountId(supplierChoice?.id ?? "");
    else if (ownerOf(accountId) !== w) {
      // A group with exactly one account picks it for you.
      const list = w === "client" ? activeAccounts : otherAccounts;
      setAccountId(list.length === 1 ? list[0]!.id : "");
    }
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/pos/${poId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      props.onSaved();
    },
    onError: (e) => setError(errText(e)),
  });

  function startEdit() {
    setDestMode("keep");
    setAddressId("");
    setVendorId("");
    setCarrier(initial.carrier);
    setService(initial.service);
    setAccountId(po.shippingAccountId ?? "");
    setWhose(null);
    setQuote(po.shippingQuote ?? "");
    setRequiredShipDate(po.requiredShipDate ?? "");
    setNotes(cleanPoNotes(po.notes) ?? "");
    setError(null);
    setEditing(true);
  }

  // Ship-by has to be before the in-hands date — not the same day, not after
  // (Stephanie 2026-10-14). The server refuses it too.
  const shipDateBad =
    requiredShipDate !== "" && !shipDateBeforeInHands(requiredShipDate, ctx?.inHandsDate ?? null);

  const quoteTrim = quote.trim().replace(/^\$/, "").replace(/,/g, "");
  const quoteBad = quoteTrim !== "" && !/^\d{1,9}(\.\d{1,2})?$/.test(quoteTrim);

  function submit() {
    if (shipDateBad || quoteBad) return;
    const body: Record<string, unknown> = {
      carrier: carrier || null,
      shipping_service: service.trim() || null,
      shipping_account_id: accountId || null,
      required_ship_date: requiredShipDate || null,
      notes: notes.trim() || null,
    };
    if (shippingQuoteReady && quoteTrim !== (po.shippingQuote ?? "")) {
      body.shipping_quote = quoteTrim === "" ? null : quoteTrim;
    }
    if (destMode === "address" && addressId) body.shipping_address_id = addressId;
    if (destMode === "vendor" && vendorId) body.ship_to_vendor_id = vendorId;
    save.mutate(body);
  }

  // A decorator's PO: the blanks come FROM the blank supplier; the decorator
  // ships the finished order to the ship-to (pilot finding #2, 2026-10-14 —
  // it used to read as if the blank supplier shipped to the client).
  const routingText =
    ctx && po.poType === "decoration_service"
      ? `Blanks arrive from ${ctx.shipFromVendorName ?? "the blank supplier"} — ${ctx.vendorName} ships the finished order to ${snap?.companyName || snap?.label || "the ship-to"}`
      : ctx && po.shipFromVendorId
        ? `Ships from ${ctx.shipFromVendorName ?? ctx.vendorName} to ${snap?.companyName || snap?.label || "the destination"}`
        : null;

  return (
    <section className="mb-6 rounded-md border border-rule bg-white">
      <div className="flex items-center justify-between border-b border-rule px-[22px] py-4">
        <h2 className="inline-flex items-center gap-2 text-lg font-semibold tracking-[-0.4px] text-ink">
          <Truck className="h-4 w-4 text-muted" /> Shipping
        </h2>
        {canManage && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit shipping
          </button>
        )}
      </div>

      {error && (
        <div className="mx-5 mt-4 rounded-md border border-risk/30 bg-risk-bg px-4 py-2 text-sm text-risk">
          {error}
        </div>
      )}

      {!editing ? (
        <div className="grid grid-cols-1 gap-6 px-5 py-4 sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Ship to
            </p>
            <div className="mt-1">
              <AddressSummary snap={snap} />
            </div>
            {routingText && (
              <p className="mt-1 text-xs font-medium text-ink">{routingText}</p>
            )}
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Ship via
            </p>
            <p className="mt-1 text-sm text-ink">{po.shippingMethod ?? "—"}</p>
            {currentHouse?.kind === "supplier" && (
              <p className="mt-1 text-sm text-muted">Ship on supplier&apos;s account</p>
            )}
            {currentHouse?.kind === "other" && (
              <p className="mt-1 text-sm text-muted">
                Bill to other&apos;s {carrierLabel(currentHouse.carrier)} account #{currentHouse.accountNumber} ({OTHER_SHIPPING_ACCOUNT_ZIP})
              </p>
            )}
            {!po.shippingAccountId && (
              <p className="mt-1 text-sm text-warn">No shipping account chosen</p>
            )}
            {po.shippingQuote && (
              <p className="mt-1 text-sm text-muted">
                Shipping price: ${Number(po.shippingQuote).toFixed(2)}
                {currentHouse?.kind === "supplier" ? " (the supplier sees it)" : ""}
              </p>
            )}
            {currentAccount && (
              <p className="mt-1 text-sm text-muted">
                Bill to client's {carrierLabel(currentAccount.carrier)} account #
                {currentAccount.accountNumber}
                {currentAccount.label ? ` (${currentAccount.label})` : ""}
              </p>
            )}
            {!currentAccount && !currentHouse && po.shippingAccountId && (
              <p className="mt-1 text-sm text-muted">
                Bill to client's carrier account (on file)
              </p>
            )}
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Required ship date
            </p>
            <p className="mt-1 text-sm text-ink">
              {formatDate(po.requiredShipDate)}
            </p>
            <p className="mt-1 text-xs text-muted">
              Client in-hands: {formatDate(ctx?.inHandsDate ?? null)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Shipping notes (visible to vendor)
            </p>
            <p className="mt-1 whitespace-pre-line text-sm text-ink">
              {cleanPoNotes(po.notes) ?? "—"}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-5 px-5 py-4">
          {/* Ship to */}
          <div>
            <p className="mb-1.5 text-xs text-muted">
              Ship to
            </p>
            <div className="mb-2 rounded-md border border-[#e7e7e4] bg-[#fafaf8] px-3 py-2">
              <p className="text-xs text-muted">Current destination</p>
              <AddressSummary snap={snap} />
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-ink">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="destMode"
                  checked={destMode === "keep"}
                  onChange={() => setDestMode("keep")}
                />
                Keep current
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="destMode"
                  checked={destMode === "address"}
                  onChange={() => setDestMode("address")}
                />
                Saved client address
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="destMode"
                  checked={destMode === "vendor"}
                  onChange={() => setDestMode("vendor")}
                />
                Vendor / decorator
              </label>
            </div>
            {destMode === "address" && (
              <select
                value={addressId}
                onChange={(e) => setAddressId(e.target.value)}
                className="mt-2 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
              >
                <option value="">Select an address…</option>
                {(addresses.data?.addresses ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} — {a.street1}, {a.city} {a.stateCode}
                  </option>
                ))}
              </select>
            )}
            {destMode === "vendor" && (
              <>
                <select
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  className="mt-2 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
                >
                  <option value="">Select a vendor / decorator…</option>
                  {(vendors.data?.rows ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
                {vendorId && (
                  <p className="mt-1 text-xs font-medium text-risk">
                    Ships from {ctx?.vendorName ?? "supplier"} to{" "}
                    {vendors.data?.rows.find((v) => v.id === vendorId)?.name ??
                      "decorator"}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Ship via */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-xs text-muted">
              Carrier
              <select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="mt-1 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
              >
                <option value="">None</option>
                {CARRIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-muted">
              Ship method (required)
              <select
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="mt-1 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
              >
                <option value="">Select…</option>
                {SERVICE_LEVELS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {service && !SERVICE_LEVELS.includes(service) && (
                  <option value={service}>{service}</option>
                )}
              </select>
            </label>
          </div>

          {/* Shipping account — required to send. Two steps (Stephanie
              2026-10-14, Jake approved): whose account, then which one. */}
          <div className="text-xs text-muted">
            Whose shipping account{house.data?.ready ? " (required)" : ""}
            <div role="radiogroup" aria-label="Whose shipping account" className="mt-1 flex w-fit rounded-full bg-canvas p-1">
              {(
                [
                  ["other", "other's"],
                  ["client", "Client's"],
                  ...(supplierChoice ? ([["supplier", "Supplier's"]] as const) : []),
                ] as Array<[AccountOwner, string]>
              ).map(([w, label]) => (
                <button
                  key={w}
                  type="button"
                  role="radio"
                  aria-checked={shownWhose === w}
                  onClick={() => pickWhose(w)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                    shownWhose === w ? "bg-ink text-white" : "text-muted hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {shownWhose === "client" || shownWhose === "other" ? (
            <label className="block text-xs text-muted">
              Which {shownWhose === "client" ? "client" : "Other"} account
              <select
                value={ownerOf(accountId) === shownWhose ? accountId : ""}
                onChange={(e) => setAccountId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
              >
                <option value="">Select…</option>
                {(shownWhose === "client" ? activeAccounts : otherAccounts).map((a) => (
                  <option key={a.id} value={a.id}>
                    {carrierLabel(a.carrier)} #{a.accountNumber}
                    {a.label ? ` (${a.label})` : ""}
                  </option>
                ))}
              </select>
              {shownWhose === "client" && activeAccounts.length === 0 && (
                <span className="mt-1 block text-xs text-warn">
                  This client has no shipping accounts on file — add one on their account page, or pick Other&apos;s.
                </span>
              )}
            </label>
          ) : shownWhose === "supplier" ? (
            <p className="text-sm text-ink">Ships on the supplier&apos;s account — no account number needed.</p>
          ) : null}

          {/* The quote box (Stephanie 2026-10-14): what the supplier quoted for
              shipping. Optional. Only the supplier's-account PO shows it to them. */}
          {shippingQuoteReady && (
            <label className="block max-w-xs text-xs text-muted">
              Shipping price (optional)
              <span className="mt-1 flex items-center gap-1">
                <span className="text-sm text-muted">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  placeholder="0.00"
                  aria-invalid={quoteBad || undefined}
                  className={`block w-full rounded-md border bg-white px-3 py-2 text-sm text-ink ${
                    quoteBad ? "border-rust" : "border-rule"
                  }`}
                />
              </span>
              <span className={`mt-1 block text-xs ${quoteBad ? "font-medium text-rust" : "text-muted"}`}>
                {quoteBad
                  ? "Use a dollar amount, e.g. 85 or 85.50."
                  : shownWhose === "supplier"
                    ? "The supplier sees this on their PO, as what they quoted."
                    : "For us only — the supplier doesn't see it."}
              </span>
            </label>
          )}

          {/* Dates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-xs text-muted">
              Required ship date
              <input
                type="date"
                value={requiredShipDate}
                onChange={(e) => setRequiredShipDate(e.target.value)}
                max={ctx?.inHandsDate ? dayBefore(ctx.inHandsDate) : undefined}
                aria-invalid={shipDateBad || undefined}
                className={`mt-1 block w-full rounded-md border bg-white px-3 py-2 text-sm text-ink ${
                  shipDateBad ? "border-rust" : "border-rule"
                }`}
              />
              {shipDateBad && (
                <span className="mt-1 block text-xs font-medium text-rust">
                  Has to be before the in-hands date ({formatDate(ctx?.inHandsDate ?? null)}).
                </span>
              )}
            </label>
            <div className="text-xs text-muted">
              Client in-hands
              <p className="mt-1 py-2 text-sm text-ink">
                {formatDate(ctx?.inHandsDate ?? null)}
              </p>
            </div>
          </div>

          {/* Shipping notes */}
          <label className="block text-xs text-muted">
            Shipping notes (visible to vendor)
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Ship blanks to decorator; do not fold."
              className="mt-1 block w-full rounded-md border border-rule px-3 py-2 text-sm text-ink"
            />
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={save.isPending || shipDateBad || quoteBad}
              className="inline-flex h-10 items-center rounded-md bg-ink px-3.5 text-sm font-medium text-white hover:bg-ink-700 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save shipping"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="inline-flex h-10 items-center rounded-md border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
